import { useEffect, useRef, useState } from "react";

import {
  createLandParcel,
  deleteLandParcel,
  listLandParcels,
  patchLandParcel,
} from "../api/landParcels";
import Field from "../../../shared/ui/Field/Field";
import Select from "../../../shared/ui/Field/Select";
import TextInput from "../../../shared/ui/Field/TextInput";
import {
  APARTMENT_TRANSITION_BLOCKED_MESSAGE,
  LAND_PURPOSE_OPTIONS,
  buildParcelWritePayload,
  getParcelEditorGuidance,
  mapParcelApiErrors,
  propertyAllowsLandParcels,
  validateParcelDraft,
} from "../lib/landParcelContract";
import "../landParcels.css";

const PURPOSE_FIELD_OPTIONS = [
  { value: "", label: "— Оберіть —" },
  ...LAND_PURPOSE_OPTIONS,
];

const editableParcel = (parcel = {}) => ({
  id: parcel.id,
  cadastral_number: parcel.cadastral_number ?? "",
  purpose_category: parcel.purpose_category ?? "",
  purpose_category_label: parcel.purpose_category_label ?? "",
  area_sotky:
    parcel.area_sotky === null || parcel.area_sotky === undefined
      ? ""
      : String(parcel.area_sotky),
  has_red_lines_or_restrictions: Boolean(parcel.has_red_lines_or_restrictions),
  created_at: parcel.created_at,
  updated_at: parcel.updated_at,
});

const rowFromParcel = (parcel) => {
  const normalized = editableParcel(parcel);
  return {
    ...normalized,
    key: `parcel-${normalized.id}`,
    isNew: false,
    dirty: false,
    errors: {},
    serverValue: normalized,
  };
};

const canonicalList = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.results)) return value.results;
  return [];
};

const reconcileRows = (
  previous,
  canonical,
  targetKey = null,
  targetId = null,
  deleting = false,
) => {
  const byId = new Map(canonical.map((parcel) => [String(parcel.id), parcel]));
  const represented = new Set();
  const next = [];

  previous.forEach((row) => {
    if (row.key === targetKey) {
      if (deleting) return;
      const saved = byId.get(String(targetId ?? row.id));
      if (saved) {
        represented.add(String(saved.id));
        next.push(rowFromParcel(saved));
      }
      return;
    }

    if (row.isNew) {
      next.push(row);
      return;
    }

    const serverParcel = byId.get(String(row.id));
    if (!serverParcel) return;
    represented.add(String(row.id));
    if (row.dirty) {
      next.push({
        ...row,
        serverValue: editableParcel(serverParcel),
      });
    } else {
      next.push(rowFromParcel(serverParcel));
    }
  });

  canonical.forEach((parcel) => {
    if (!represented.has(String(parcel.id))) {
      next.push(rowFromParcel(parcel));
    }
  });

  return next;
};

function LandParcelsEditor({
  propertyId,
  propertyType,
  initialParcels,
  onParcelsChange,
  disabled = false,
  apartmentTransitionBlocked = null,
  api = null,
}) {
  const listFn = api?.listLandParcels || listLandParcels;
  const createFn = api?.createLandParcel || createLandParcel;
  const patchFn = api?.patchLandParcel || patchLandParcel;
  const deleteFn = api?.deleteLandParcel || deleteLandParcel;
  const [rows, setRows] = useState(() =>
    canonicalList(initialParcels).map(rowFromParcel),
  );
  const [pendingKey, setPendingKey] = useState(null);
  const [deleteConfirmKey, setDeleteConfirmKey] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const localKeyRef = useRef(0);
  const mutationPendingRef = useRef(false);
  const confirmButtonRef = useRef(null);

  useEffect(() => {
    const canonical = canonicalList(initialParcels);
    setRows((previous) => reconcileRows(previous, canonical));
  }, [initialParcels]);

  useEffect(() => {
    if (deleteConfirmKey && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [deleteConfirmKey]);

  const blockedMessage =
    typeof apartmentTransitionBlocked === "string"
      ? apartmentTransitionBlocked
      : apartmentTransitionBlocked
        ? APARTMENT_TRANSITION_BLOCKED_MESSAGE
        : null;

  if (!propertyAllowsLandParcels(propertyType)) {
    const hasUnexpectedParcels =
      canonicalList(initialParcels).length > 0 || rows.some((row) => !row.isNew);
    if (propertyType !== "apartment" || (!hasUnexpectedParcels && !blockedMessage)) {
      return null;
    }
    return (
      <section className="land-parcels-editor" aria-labelledby="land-parcels-editor-title">
        <header className="land-parcels-editor__head">
          <h3 id="land-parcels-editor-title">Кадастрові ділянки</h3>
        </header>
        <p
          className="land-parcels-editor__message land-parcels-editor__message--warning"
          role="alert"
        >
          {blockedMessage || APARTMENT_TRANSITION_BLOCKED_MESSAGE}
        </p>
      </section>
    );
  }

  const mutationsDisabled = disabled || !propertyId || mutationPendingRef.current;
  const guidance = getParcelEditorGuidance(propertyType);

  const setRowField = (key, field, value) => {
    setRows((previous) =>
      previous.map((row) =>
        row.key === key
          ? {
              ...row,
              [field]: value,
              dirty: true,
              errors: { ...row.errors, [field]: undefined, _form: undefined },
            }
          : row,
      ),
    );
  };

  const addRow = () => {
    if (mutationsDisabled) return;
    localKeyRef.current += 1;
    setRows((previous) => [
      ...previous,
      {
        ...editableParcel(),
        key: `new-${localKeyRef.current}`,
        isNew: true,
        dirty: true,
        errors: {},
        serverValue: null,
      },
    ]);
    setStatusMessage("");
  };

  const notifyChange = async (parcels) => {
    if (!onParcelsChange) return;
    try {
      await onParcelsChange(parcels);
    } catch {
      // The mutation and authoritative editor refresh already succeeded.
      // Parent reconciliation failure must not turn it into a duplicate retry.
    }
  };

  const refreshAfterMutation = async (
    fallback,
    targetKey,
    targetId,
    deleting = false,
  ) => {
    let canonical = fallback;
    try {
      canonical = canonicalList(await listFn(propertyId));
    } catch {
      // Mutation responses are canonical rows; keep their safe fallback when
      // the follow-up list request is temporarily unavailable.
    }
    setRows((previous) =>
      reconcileRows(previous, canonical, targetKey, targetId, deleting),
    );
    await notifyChange(canonical);
  };

  const saveRow = async (row) => {
    if (mutationsDisabled || mutationPendingRef.current) return;
    const validation = validateParcelDraft(row);
    if (!validation.ok) {
      setRows((previous) =>
        previous.map((item) =>
          item.key === row.key ? { ...item, errors: validation.errors } : item,
        ),
      );
      return;
    }

    mutationPendingRef.current = true;
    setPendingKey(row.key);
    setStatusMessage("");
    try {
      const payload = buildParcelWritePayload(row);
      const saved = row.isNew
        ? await createFn(propertyId, payload)
        : await patchFn(propertyId, row.id, payload);
      const fallback = rows
        .filter((item) => !item.isNew && item.key !== row.key)
        .map((item) => item.serverValue || editableParcel(item))
        .concat(saved);
      await refreshAfterMutation(fallback, row.key, saved.id);
      setStatusMessage(
        row.isNew
          ? "Кадастрову ділянку додано."
          : "Зміни кадастрової ділянки збережено.",
      );
    } catch (error) {
      const errors = mapParcelApiErrors(error);
      setRows((previous) =>
        previous.map((item) =>
          item.key === row.key ? { ...item, errors } : item,
        ),
      );
    } finally {
      mutationPendingRef.current = false;
      setPendingKey(null);
    }
  };

  const cancelRow = (row) => {
    if (mutationsDisabled) return;
    setDeleteConfirmKey(null);
    setRows((previous) =>
      row.isNew
        ? previous.filter((item) => item.key !== row.key)
        : previous.map((item) =>
            item.key === row.key
              ? {
                  ...rowFromParcel(item.serverValue),
                  key: item.key,
                }
              : item,
          ),
    );
  };

  const confirmDelete = async (row) => {
    if (mutationsDisabled || row.isNew || mutationPendingRef.current) return;
    mutationPendingRef.current = true;
    setPendingKey(row.key);
    setStatusMessage("");
    try {
      await deleteFn(propertyId, row.id);
      const fallback = rows
        .filter((item) => !item.isNew && item.key !== row.key)
        .map((item) => item.serverValue || editableParcel(item));
      await refreshAfterMutation(fallback, row.key, row.id, true);
      setDeleteConfirmKey(null);
      setStatusMessage("Кадастрову ділянку видалено.");
    } catch (error) {
      const errors = mapParcelApiErrors(error);
      setRows((previous) =>
        previous.map((item) =>
          item.key === row.key ? { ...item, errors } : item,
        ),
      );
      setDeleteConfirmKey(null);
    } finally {
      mutationPendingRef.current = false;
      setPendingKey(null);
    }
  };

  return (
    <section className="land-parcels-editor" aria-labelledby="land-parcels-editor-title">
      <header className="land-parcels-editor__head">
        <h3 id="land-parcels-editor-title">Кадастрові ділянки</h3>
        <p>{guidance}</p>
      </header>

      {!propertyId ? (
        <p className="land-parcels-editor__message" role="status">
          Кадастрові ділянки можна зберегти після створення чернетки оголошення.
        </p>
      ) : null}
      {blockedMessage ? (
        <p
          className="land-parcels-editor__message land-parcels-editor__message--warning"
          role="alert"
        >
          {blockedMessage}
        </p>
      ) : null}

      <div className="land-parcels-editor__rows">
        {rows.map((row, index) => {
          const prefix = `land-parcel-${row.key}`;
          const isPending = pendingKey === row.key;
          return (
            <section
              className="land-parcels-editor__row"
              key={row.key}
              role="group"
              aria-labelledby={`${prefix}-title`}
            >
              <h4
                id={`${prefix}-title`}
                className="land-parcels-editor__row-title"
              >
                Кадастрова ділянка {index + 1}
              </h4>
              <div className="land-parcels-editor__fields">
                <Field
                  id={`${prefix}-cadastral`}
                  label="Кадастровий номер"
                  error={row.errors.cadastral_number}
                  className="land-parcels-editor__field"
                >
                  <TextInput
                    type="text"
                    placeholder="2610100000:01:001:0001"
                    value={row.cadastral_number}
                    onChange={(event) =>
                      setRowField(row.key, "cadastral_number", event.target.value)
                    }
                    autoComplete="off"
                    disabled={disabled || isPending}
                  />
                </Field>
                <Field
                  id={`${prefix}-category`}
                  label="Категорія цільового призначення"
                  error={row.errors.purpose_category}
                  className="land-parcels-editor__field"
                >
                  <Select
                    value={row.purpose_category}
                    onChange={(event) =>
                      setRowField(row.key, "purpose_category", event.target.value)
                    }
                    options={PURPOSE_FIELD_OPTIONS}
                    disabled={disabled || isPending}
                  />
                </Field>
                <Field
                  id={`${prefix}-area`}
                  label="Площа ділянки, сотки (необов’язково)"
                  help="1 сотка = 100 м²"
                  error={row.errors.area_sotky}
                  className="land-parcels-editor__field"
                >
                  <TextInput
                    type="text"
                    inputMode="decimal"
                    value={row.area_sotky}
                    onChange={(event) =>
                      setRowField(row.key, "area_sotky", event.target.value)
                    }
                    autoComplete="off"
                    disabled={disabled || isPending}
                  />
                </Field>
                <div className="land-parcels-editor__field land-parcels-editor__field--wide">
                  <label
                    className="check-card land-parcels-editor__restriction"
                    htmlFor={`${prefix}-red-lines`}
                  >
                    <input
                      id={`${prefix}-red-lines`}
                      type="checkbox"
                      checked={Boolean(row.has_red_lines_or_restrictions)}
                      disabled={disabled || isPending}
                      onChange={(event) =>
                        setRowField(
                          row.key,
                          "has_red_lines_or_restrictions",
                          event.target.checked,
                        )
                      }
                      aria-invalid={
                        row.errors.has_red_lines_or_restrictions
                          ? "true"
                          : undefined
                      }
                      aria-describedby={
                        row.errors.has_red_lines_or_restrictions
                          ? `${prefix}-red-lines-error`
                          : undefined
                      }
                    />
                    <span className="check-card__icon" aria-hidden="true">
                      <i className="fa-solid fa-triangle-exclamation" />
                    </span>
                    <span className="check-card__body">
                      <strong>Є червоні лінії або інші обмеження</strong>
                    </span>
                  </label>
                  {row.errors.has_red_lines_or_restrictions ? (
                    <small
                      id={`${prefix}-red-lines-error`}
                      className="land-parcels-editor__error"
                    >
                      {row.errors.has_red_lines_or_restrictions}
                    </small>
                  ) : null}
                </div>
              </div>

              {row.errors._form ? (
                <p className="land-parcels-editor__error" role="alert">
                  {row.errors._form}
                </p>
              ) : null}

              <div className="land-parcels-editor__actions">
                <button
                  type="button"
                  className="action-btn action-btn--accent"
                  onClick={() => saveRow(row)}
                  disabled={mutationsDisabled || isPending}
                >
                  {isPending
                    ? "Збереження…"
                    : row.isNew
                      ? "Зберегти ділянку"
                      : "Зберегти зміни"}
                </button>
                <button
                  type="button"
                  className="action-btn"
                  onClick={() => cancelRow(row)}
                  disabled={mutationsDisabled}
                >
                  Скасувати
                </button>
                {!row.isNew ? (
                  <button
                    type="button"
                    className="action-btn action-btn--danger"
                    onClick={() => setDeleteConfirmKey(row.key)}
                    disabled={mutationsDisabled}
                  >
                    Видалити ділянку
                  </button>
                ) : null}
              </div>

              {deleteConfirmKey === row.key ? (
                <div
                  className="land-parcels-editor__delete-confirm"
                  role="alertdialog"
                  aria-label={`Видалення ділянки ${row.cadastral_number}`}
                >
                  <p>
                    Видалити кадастрову ділянку з номером{" "}
                    <strong className="land-parcels-editor__cadastral-value">
                      {row.cadastral_number}
                    </strong>
                    ?
                  </p>
                  <div className="land-parcels-editor__delete-confirm-actions">
                    <button
                      ref={confirmButtonRef}
                      type="button"
                      className="action-btn action-btn--danger"
                      onClick={() => confirmDelete(row)}
                      disabled={mutationsDisabled}
                    >
                      Підтвердити видалення
                    </button>
                    <button
                      type="button"
                      className="action-btn"
                      onClick={() => setDeleteConfirmKey(null)}
                      disabled={mutationsDisabled}
                    >
                      Скасувати видалення
                    </button>
                  </div>
                </div>
              ) : null}
            </section>
          );
        })}
      </div>

      <button
        type="button"
        className="action-btn land-parcels-editor__add"
        onClick={addRow}
        disabled={mutationsDisabled || Boolean(blockedMessage)}
      >
        <i className="fa-solid fa-plus" aria-hidden="true" />
        Додати кадастрову ділянку
      </button>

      <div aria-live="polite">
        {statusMessage ? (
          <p className="land-parcels-editor__message">{statusMessage}</p>
        ) : null}
      </div>
    </section>
  );
}

export default LandParcelsEditor;
